import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Monitor, 
  Server, 
  Database, 
  Code, 
  Calendar,
  CheckCircle,
  Clock,
  Shield,
  User
} from "lucide-react";

const API = '/api';

// Application version info - update these when releasing new versions
// BUILD_NUMBER: Increment by 1 for each new build
const APP_VERSION = "2.1";
const BUILD_NUMBER = "1367";
const RELEASE_DATE = "February 14, 2026";

export default function System() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getLicenseStatus = () => {
    if (!settings?.license_expires) return { status: "unknown", color: "slate" };
    
    const expiresDate = new Date(settings.license_expires);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiresDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: "Expired", color: "red" };
    } else if (daysUntilExpiry <= 30) {
      return { status: `Expires in ${daysUntilExpiry} days`, color: "yellow" };
    } else {
      return { status: "Active", color: "green" };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  const licenseStatus = getLicenseStatus();

  return (
    <div data-testid="system-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight flex items-center gap-3">
          <Monitor className="text-[#1B3B6F]" />
          System Information
        </h1>
        <p className="text-slate-500 mt-1">Application version and system details</p>
      </div>

      {/* Quick Info Banner */}
      <Card className="bg-gradient-to-r from-[#1B3B6F] to-[#2a4a7f] text-white">
        <CardContent className="py-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wide">Admin</p>
              <p className="font-semibold text-lg">{user?.name || "N/A"}</p>
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wide">Version</p>
              <p className="font-semibold text-lg">v{APP_VERSION}</p>
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wide">Build</p>
              <p className="font-semibold text-lg">{BUILD_NUMBER}</p>
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wide">License Expires</p>
              <p className="font-semibold text-lg">{formatDate(settings?.license_expires) || "N/A"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Version Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Code className="text-[#1B3B6F]" />
              Application Version
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Version</span>
              <Badge className="bg-[#1B3B6F] text-white text-lg px-3 py-1">
                v{APP_VERSION}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Build Number</span>
              <span className="font-mono text-slate-800">{BUILD_NUMBER}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600 flex items-center gap-2">
                <Calendar size={16} />
                Release Date
              </span>
              <span className="text-slate-800">{RELEASE_DATE}</span>
            </div>
          </CardContent>
        </Card>

        {/* License Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Shield className="text-[#1B3B6F]" />
              License Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Status</span>
              <Badge className={`
                ${licenseStatus.color === 'green' ? 'bg-green-100 text-green-800' : ''}
                ${licenseStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' : ''}
                ${licenseStatus.color === 'red' ? 'bg-red-100 text-red-800' : ''}
                ${licenseStatus.color === 'slate' ? 'bg-slate-100 text-slate-800' : ''}
              `}>
                {licenseStatus.color === 'green' && <CheckCircle size={14} className="mr-1" />}
                {licenseStatus.color === 'yellow' && <Clock size={14} className="mr-1" />}
                {licenseStatus.status}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">License Type</span>
              <span className="font-medium text-slate-800 capitalize">
                {settings?.license_type || "Not configured"}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Expires</span>
              <span className="text-slate-800">
                {formatDate(settings?.license_expires)}
              </span>
            </div>

            {settings?.license_owner && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Licensed To</span>
                <span className="text-slate-800">{settings.license_owner}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* System Limits Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Database className="text-[#1B3B6F]" />
              System Limits
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Max Users</span>
              <span className="font-mono text-slate-800">
                {settings?.max_users?.toLocaleString() || "Unlimited"}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Max Events</span>
              <span className="font-mono text-slate-800">
                {settings?.max_events?.toLocaleString() || "Unlimited"}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Technical Details Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Server className="text-[#1B3B6F]" />
              Technical Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Frontend</span>
              <span className="text-slate-800">React 18</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Backend</span>
              <span className="text-slate-800">PHP 8.x</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Database</span>
              <span className="text-slate-800">MySQL / MariaDB</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Hosting</span>
              <span className="text-slate-800">Bluehost Shared</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer Note */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-4">
          <p className="text-sm text-slate-500 text-center">
            Trap Tracker by NMLRA • For support, contact your system administrator
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
